import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmCircularProgress from '../ui/OmCircularProgress';
import bankService from '../../services/bankService';

/**
 * Searchable Bank Branch Dropdown Component
 * 
 * Provides a searchable dropdown for selecting bank branches using the Om Design System.
 * Displays options in the format "BANK_NAME, BRANCH_NAME" and stores the branch ID as value.
 * Supports SLAMS auto-population and manual user selection.
 */
const BankBranchSelect = ({
  label = "Bank & Branch",
  name = "bankBranch",
  value = "",
  onChange,
  onBankInfoSelected,
  disabled = false,
  required = false,
  error,
  ...props
}) => {
  const [bankBranchOptions, setBankBranchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load bank branch options on component mount
  useEffect(() => {
    const loadBankBranchOptions = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        
        
        const options = await bankService.getBankBranchOptions();
        
        
        setBankBranchOptions(options);
        
      } catch (error) {
        console.error('❌ BankBranchSelect: Failed to load bank branch options:', error);
        setLoadError('Failed to load bank options. Please try again.');
        setBankBranchOptions([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBankBranchOptions();
  }, []);

  // Handle selection change
  const handleSelectionChange = (event) => {
    const selectedValue = event.target.value;
    
    
    // Find the selected option details
    const selectedOption = bankBranchOptions.find(opt => opt.value === selectedValue);
    
    if (selectedOption) {
      
      // Call the onChange handler with the selected value
      if (onChange) {
        onChange(event);
      }
      
      // Call the bank info callback with detailed information
      if (onBankInfoSelected) {
        onBankInfoSelected({
          branchId: selectedOption.value,
          bankCode: selectedOption.bankCode,
          bankName: selectedOption.bankName,
          branchName: selectedOption.branchName,
          displayText: selectedOption.label
        });
      }
    } else {
      console.warn('⚠️ BankBranchSelect: Selected option not found:', selectedValue);
    }
  };

  // Auto-select option based on bank code and branch ID (for SLAMS integration)
  const autoSelectOption = async (bankCode, branchId) => {
    try {
      
      const matchingOption = await bankService.findBankBranchOption(bankCode, branchId);
      
      if (matchingOption) {
        
        // Simulate selection change event
        const syntheticEvent = {
          target: { value: matchingOption.value, name: name }
        };
        
        handleSelectionChange(syntheticEvent);
        return matchingOption.value;
      } else {
        console.warn('⚠️ BankBranchSelect: No matching option found for auto-selection');
        return null;
      }
    } catch (error) {
      console.error('❌ BankBranchSelect: Auto-selection failed:', error);
      return null;
    }
  };

  // Expose autoSelectOption method to parent components
  useEffect(() => {
    if (props.ref && typeof props.ref === 'object') {
      props.ref.current = {
        autoSelectOption
      };
    }
  }, [bankBranchOptions]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md bg-gray-50">
          <OmCircularProgress 
            indeterminate={true}
            size="small"
          />
          <span className="text-sm text-gray-600">Loading bank options...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (loadError) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="p-3 border border-red-300 rounded-md bg-red-50">
          <p className="text-sm text-red-600">{loadError}</p>
          <button 
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 text-xs text-red-700 underline hover:text-red-900"
          >
            Reload page to try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <OmSelect
        label={label}
        name={name}
        value={value}
        onChange={handleSelectionChange}
        disabled={disabled || bankBranchOptions.length === 0}
        required={required}
        supportingText="Type to search banks and branches"
        customValidationMessage={error}
        {...props}
      >
        <OmSelectOption value="">
          Select bank and branch...
        </OmSelectOption>
        
        {bankBranchOptions.map(option => (
          <OmSelectOption 
            key={option.value}
            value={option.value}
            displayText={option.label}
            typeaheadText={option.searchTerms.join(' ')}
          >
            {option.label}
          </OmSelectOption>
        ))}
      </OmSelect>
      
      {bankBranchOptions.length === 0 && !isLoading && !loadError && (
        <p className="text-sm text-gray-500">No bank options available</p>
      )}
    </div>
  );
};

BankBranchSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBankInfoSelected: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string
};

export default BankBranchSelect;